import React from 'react';
import { classnames } from '@/utils/classnames/classnames';
import MarkdownRenderer from './components/markdown-renderer';
import {
  RichTextBackground,
  RichTextContainerWidth,
  RichTextContentAlignment,
  RichTextContentBlockProps,
  RichTextVerticalSpacing,
} from './types';

const widthClasses: Record<RichTextContainerWidth, string> = {
  narrow: 'max-w-2xl',
  standard: 'max-w-4xl',
  wide: 'max-w-6xl',
  full: 'max-w-none',
};

const backgroundClasses: Record<RichTextBackground, string> = {
  white: 'bg-white',
  'light-grey': 'bg-neutral-200',
  // Theme has no numeric `primary-900`; the brand navy is `bg-primary` here.
  navy: 'bg-primary',
};

// Mobile spacing ~33% lighter than desktop, per spec.
const verticalSpacingClasses: Record<RichTextVerticalSpacing, string> = {
  none: '',
  sm: 'py-8 md:py-12',
  md: 'py-12 md:py-20',
  lg: 'py-16 md:py-28',
};

const alignmentClasses: Record<RichTextContentAlignment, string> = {
  left: '',
  // Centre headings + top-level paragraphs only; lists/tables/blockquotes stay left.
  center: 'text-center [&_blockquote]:text-left [&_ol]:text-left [&_table]:text-left [&_ul]:text-left',
};

/**
 * RichTextContentBlock — renders an authored Markdown block with author-tunable
 * container width, background, vertical spacing, and alignment. Backs the
 * homepage "Welcome to Electus Distribution" intro and is reusable on any
 * content page. Renders nothing when `content` is empty (no spacing artefact).
 */
const RichTextContentBlock = ({
  content,
  containerWidth = 'standard',
  background = 'white',
  verticalSpacing = 'md',
  contentAlignment = 'left',
  proseSize = 'base',
  className,
}: RichTextContentBlockProps) => {
  if (!content?.trim()) return null;

  const invert = background === 'navy';

  return (
    <section className={classnames(backgroundClasses[background], verticalSpacingClasses[verticalSpacing], className)}>
      <div className={classnames('mx-auto px-4 md:px-6 lg:px-8', widthClasses[containerWidth])}>
        <div
          className={classnames(
            'prose max-w-none',
            { 'prose-sm': proseSize === 'sm' },
            // No `prose-a:no-underline` — links own their own `underline`
            // (markdown-renderer linkClassName); the AC requires a visible
            // underline, and the prose variant would otherwise win on cascade order.
            'prose-headings:font-semibold',
            invert ? 'prose-invert prose-headings:text-white' : 'prose-headings:text-primary',
            alignmentClasses[contentAlignment],
          )}
        >
          <MarkdownRenderer content={content} invert={invert} />
        </div>
      </div>
    </section>
  );
};

export default RichTextContentBlock;
